"use client";

// Leadership grid — client island that renders the leader cards and owns the
// bio-modal open state. Clicking a portrait opens the modal (Figma annotation:
// "Modal will appear once the user clicks the 'leadership' image").

import { useState } from "react";

import { cn } from "@/lib/utils";

import { LeadershipModal } from "./leadership-modal";
import { LeadershipPortrait } from "./leadership-portrait";
import type { Leader } from "./leadership-section";
import { LinkedInIcon } from "./linkedin-icon";

interface LeadershipGridProps {
  leaders: Leader[];
  /** Accessible label for the LinkedIn link on each card. */
  linkedinLabel: string;
  /** Accessible label for the modal close button. */
  closeLabel: string;
}

export function LeadershipGrid({
  leaders,
  linkedinLabel,
  closeLabel,
}: LeadershipGridProps) {
  const [active, setActive] = useState<Leader | null>(null);

  return (
    <>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {leaders.map((leader) => (
          <LeaderCard
            key={leader.id}
            leader={leader}
            linkedinLabel={linkedinLabel}
            onOpen={() => setActive(leader)}
          />
        ))}
      </div>

      <LeadershipModal
        leader={active}
        closeLabel={closeLabel}
        onClose={() => setActive(null)}
      />
    </>
  );
}

function LeaderCard({
  leader,
  linkedinLabel,
  onOpen,
}: {
  leader: Leader;
  linkedinLabel: string;
  onOpen: () => void;
}) {
  const teaser = leader.teaser ?? firstSentence(leader.description);
  return (
    <article className="group flex flex-col gap-6 lg:gap-[23px]">
      <button
        type="button"
        onClick={onOpen}
        aria-label={leader.bioLabel ?? leader.name}
        className={cn(
          "bg-grey-200 border-grey-100 rounded-ds-xl focus-visible:ring-pulse-700 relative h-[220px] w-full cursor-pointer overflow-hidden border text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:size-40 lg:h-[358px] lg:w-full",
          "[--image-blur-reveal:0%] group-hover:[--image-blur-reveal:45%] motion-safe:transition-[--image-blur-reveal] motion-safe:duration-[1200ms] motion-safe:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
        )}
      >
        {leader.portraits ? (
          <LeadershipPortrait
            src={leader.portraits.cardSrc}
            alt={leader.image?.alt ?? leader.name}
            mobile={leader.portraits.card.mobile}
            tablet={leader.portraits.card.tablet}
            desktop={leader.portraits.card.desktop}
          />
        ) : leader.image?.src ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={leader.image.src}
            alt={leader.image.alt}
            className="size-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="bg-grey-200 size-full" />
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 backdrop-blur-[4px]"
          style={{
            maskImage:
              "linear-gradient(to top, black max(calc(var(--image-blur-reveal) - 20%), 0%), transparent var(--image-blur-reveal))",
            WebkitMaskImage:
              "linear-gradient(to top, black max(calc(var(--image-blur-reveal) - 20%), 0%), transparent var(--image-blur-reveal))",
          }}
        />
      </button>

      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-ds-heading-h5 text-grey-950">{leader.name}</h3>
          {leader.linkedinUrl && (
            <a
              href={leader.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${linkedinLabel} — ${leader.name}`}
              className="text-grey-600 hover:text-pulse-700 mt-0.5 shrink-0 transition-colors duration-200"
            >
              <LinkedInIcon className="size-6" />
            </a>
          )}
        </div>
        <p className="text-ds-body-sm text-grey-600 font-medium">
          {leader.position}
        </p>
        {teaser && <p className="text-ds-body-sm text-grey-600">{teaser}</p>}
      </div>
    </article>
  );
}

function firstSentence(text: string): string {
  const trimmed = text.trim();
  const match = trimmed.match(/^(.+?[.!?])\s/);
  return match ? match[1]! : trimmed.slice(0, 160);
}
